import React, { useState, useMemo } from "react";
import { NavLink, Outlet, Link } from "react-router-dom";
import { LayoutDashboard, Users, Package, Warehouse, Upload, CircleDot, LogOut, History, RefreshCw, FileText, Table2, BadgeEuro } from "lucide-react";
import { toast } from "sonner";
import { api, logout } from "@/lib/api";

const navItems = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/depositi", label: "Depositi", icon: Package, testid: "nav-depositi" },
  { to: "/magazzino", label: "Magazzino", icon: Warehouse, testid: "nav-magazzino" },
  { to: "/clienti", label: "Clienti", icon: Users, testid: "nav-clienti" },
  { to: "/audit", label: "Storico", icon: History, testid: "nav-audit" },
  { to: "/import", label: "Importa", icon: Upload, testid: "nav-import" },
];

const Layout = React.memo(function Layout() {
  const [updateChecking, setUpdateChecking] = useState(false);

  const forceClientRefresh = async () => {
    try {
      // Clear browser-managed caches and service workers so fresh assets are fetched.
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch (_) {
      // Ignore cache cleanup errors and continue with hard reload.
    }
    window.location.reload();
  };

  const checkForUpdates = async () => {
    setUpdateChecking(true);
    try {
      const { data } = await api.get("/admin/check-updates");
      if (data.update_available) {
        if (!data.auto_update_enabled && data.download_url) {
          const confirmDownload = window.confirm(
            `Nuove modifiche ${data.latest} disponibili. Vuoi scaricarle ora?`
          );
          if (!confirmDownload) {
            toast(`Aggiornamento disponibile: ${data.latest}`);
            return;
          }

          window.open(data.download_url, "_blank", "noopener,noreferrer");
          toast.success("Download modifiche avviato. Dopo l'installazione, ricarica la pagina.");
          return;
        }

        if (!data.auto_update_enabled) {
          toast.error(
            `Aggiornamento ${data.latest} disponibile, ma auto-update disabilitato. Attiva ENABLE_AUTO_UPDATE e monta /var/run/docker.sock.`
          );
          return;
        }

        const confirmInstall = window.confirm(
          `Aggiornamento ${data.latest} disponibile. Vuoi installarlo ora?`
        );
        if (!confirmInstall) {
          toast(`Aggiornamento disponibile: ${data.latest}`);
          return;
        }

        const { data: runResult } = await api.post("/admin/run-update");
        toast.success(runResult.message || "Aggiornamento avviato. Attendi e ricarica.");
      } else {
        toast.success(`Nessun aggiornamento disponibile. Versione corrente: ${data.current}`);
      }
    } catch (err) {
      toast.error("Server aggiornamenti non raggiungibile. Forzo ricarica con cache pulita...");
      await forceClientRefresh();
    } finally {
      setUpdateChecking(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Top header */}
      <header className="border-b-4 border-black bg-white sticky top-0 z-40">
        <div className="diag-stripes h-2 w-full" />
        <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-logo">
            <div className="w-11 h-11 bg-black flex items-center justify-center border-2 border-black">
              <CircleDot className="w-6 h-6 text-[#E60000]" strokeWidth={3} />
            </div>
            <div className="leading-none">
              <div className="font-display font-black text-xl md:text-2xl uppercase tracking-tighter">
                MAGAZZINO CLIENTI
              </div>
              <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-black/70 mt-1">
                Gestione Pneumatici in Tempo Reale
              </div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={n.testid}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-4 py-2.5 border-2 border-black font-bold uppercase text-xs tracking-widest transition-colors ${
                    isActive
                      ? "bg-[#E60000] text-white"
                      : "bg-white text-black hover:bg-black hover:text-white"
                  }`
                }
              >
                <n.icon className="w-4 h-4" strokeWidth={2.5} />
                {n.label}
              </NavLink>
            ))}
            <button
              onClick={checkForUpdates}
              data-testid="check-updates-button"
              title="Verifica aggiornamenti"
              disabled={updateChecking}
              className="flex items-center gap-2 px-4 py-2.5 border-2 border-black font-bold uppercase text-xs tracking-widest bg-white text-black hover:bg-black hover:text-white transition-colors"
            >
              <RefreshCw className="w-4 h-4" strokeWidth={2.5} />
              {updateChecking ? "Controllo..." : "Aggiorna"}
            </button>
            <button
              onClick={logout}
              data-testid="logout-button"
              title="Esci"
              className="flex items-center gap-2 px-4 py-2.5 border-2 border-black font-bold uppercase text-xs tracking-widest bg-white text-black hover:bg-[#E60000] hover:text-white transition-colors"
            >
              <LogOut className="w-4 h-4" strokeWidth={2.5} />
              Esci
            </button>
          </nav>
        </div>
        {/* Mobile nav */}
        <nav className="md:hidden border-t-2 border-black flex overflow-x-auto">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`m-${n.testid}`}
              className={({ isActive }) =>
                `flex-1 min-w-[80px] flex flex-col items-center justify-center gap-1 px-2 py-2 font-bold uppercase text-[10px] tracking-wider border-r-2 border-black last:border-r-0 ${
                  isActive ? "bg-[#E60000] text-white" : "bg-white text-black"
                }`
              }
            >
              <n.icon className="w-4 h-4" strokeWidth={2.5} />
              {n.label}
            </NavLink>
          ))}
          <button
            onClick={checkForUpdates}
            data-testid="m-check-updates-button"
            title="Verifica aggiornamenti"
            disabled={updateChecking}
            className="flex-1 min-w-[80px] flex flex-col items-center justify-center gap-1 px-2 py-2 font-bold uppercase text-[10px] tracking-wider border-r-2 border-black last:border-r-0 bg-white text-black hover:bg-black hover:text-white"
          >
            <RefreshCw className="w-4 h-4" strokeWidth={2.5} />
            {updateChecking ? "..." : "Aggiorna"}
          </button>
        </nav>
      </header>

      <main className="max-w-[1600px] mx-auto px-4 md:px-8 py-6 md:py-10">
        <Outlet />
      </main>

      <footer className="border-t-4 border-black bg-white mt-12">
        <div className="diag-stripes h-2 w-full" />
        <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-6 flex items-center justify-between">
          <div className="text-[10px] uppercase font-bold tracking-[0.25em]">
            Magazzino Clienti • Vasto & Cupello
          </div>
          <div className="font-mono-data text-xs">v1.0</div>
        </div>
      </footer>
    </div>
  );
});

export default Layout;
